import {
  faBox,
  faClipboardList,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BoomBox } from "lucide-react";
import React, { useState } from "react";
import { toast } from "react-toastify";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import httpRequest from "src/helpers/httpRequest";
import { CURRENCY, ImageBaseUrl } from "src/lib/constans";
import { IPharmaInventory } from "src/types/GlobalInterfaces";
import { useNavigate } from "react-router-dom";

interface IProps {
  payload: any;
  setActiveTab: (tab: string) => void;
  allProducts: IPharmaInventory[];
  allPharmaProducts: IPharmaInventory[];
  productQuantity: {
    [key: string]: number;
  };
  errors: {
    [key: string]: string;
  };
  validateAddressDetailsForm: () => boolean;
  validatePersonalDetailsForm: () => boolean;
}

const PrintProduct = ({ product, index }: { product: any; index: number }) => {
  return (
    <div className="w-full flex gap-2">
      <div className="w-[70px] aspect-[1/0.9] overflow-hidden rounded-lg border border-gray-300">
        <img
          src={`${ImageBaseUrl}${product?.images}`}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex flex-col gap-2 justify-center w-[calc(100%-80px)]">
        <p className="font-Poppins-Regular truncate font-size-16px theme-color-green">
          {product?.productName}
        </p>
        <div className="font-Poppins-Medium flex justify-between -mt-[5px] font-size-14px theme-color-gray">
          <p>
          {CURRENCY}{" "}
            {parseFloat(
              product?.salePrice || product?.regularPrice || "0"
            ).toFixed(2)}
          </p>
          <p>Quantity: {String(product?.quantity || 1).padStart(2, "0")}</p>
        </div>
      </div>
    </div>
  );
};

const Box = ({
  title,
  icon,
  children,
  className,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={`w-full box-design p-3 bg-white rounded-lg ${className}`}>
      <div className="flex gap-2 items-center border-b text-[#202020] border-gray-200 pb-3 w-full mb-3">
        {icon}
        <p className="font-Poppins-Medium font-size-18px ">{title}</p>
      </div>
      {children}
    </div>
  );
};

const DetailsLabelValue = ({
  label,
  value,
  className,
  labelClassName,
}: {
  label: string;
  value: string;
  className?: string;
  labelClassName?: string;
}) => {
  return (
    <div className="flex justify-between items-center gap-2 w-full">
      <p
        className={`font-Poppins-Medium max-w-[35%] font-size-16px text-[#2F2F31] ${labelClassName}`}
      >
        {label}
      </p>
      <p
        className={`font-Poppins-Regular text-right max-w-[70%] font-size-15px text-[#303030] ${className}`}
      >
        {value || "-"}
      </p>
    </div>
  );
};

const OrderOverview = ({
  payload,
  setActiveTab,
  allPharmaProducts,
  productQuantity,
  errors,
  validateAddressDetailsForm,
  validatePersonalDetailsForm,
}: IProps) => {
  const [isLoader, setIsLoader] = useState(false);
  const navigate = useNavigate();
  const confirmOrder = async () => {

    let addressDetails = {
      customerId : payload.userDetails._id,
      addressType : payload.address,
      emirate : payload.emirate,
      city : payload.city,
      streetAddress : payload.streetAddress,
      flatNo : payload.flatVillaNo,
      buildingName : payload.buildingName,
      country : payload.country,
      mobileNumber : payload.mobileNumber,
      personName : payload.firstName,
      landmarks : payload.additionalInfo,
      isCurrentSelect : true,
    }

    if(!validateAddressDetailsForm()){
      setActiveTab("Address Details");
      return;
    }
    if(!validatePersonalDetailsForm()){
      toast(errors.firstName, { type: "error" });
      setActiveTab("Personal Details");
      return;
    }
    if (payload.products.length === 0) {
      toast("Please add products to the cart", { type: "error" });
      setActiveTab("Add Products");
      return;
    }
    const formData = new FormData();
    formData.append(
      "totalAmount",
      String(
        payload.products
          .map((product: any) =>
            allPharmaProducts.find((item: IPharmaInventory) => item._id === product)
          )
          .reduce(
            (acc: number, curr: any) =>
              acc +
              parseFloat(curr?.salePrice || curr?.regularPrice || "0") *
                (productQuantity[curr?._id] || 1),
            0
          )
      )
    );
    formData.append("addressDetail", JSON.stringify(addressDetails));
    formData.append("shippingAddress", payload.streetAddress || "");
    formData.append(
      "items",
      JSON.stringify(
        payload.products?.map((v: any) => ({
          _id: v,
          quantity: productQuantity[v] || 1,
          itemType: "pharmacy",
        }))
      )
    );
    formData.append("name", payload.firstName || "");
    formData.append("gender", payload?.userDetails?.gender || "");
    formData.append("mobile", payload.mobileNumber || "");
    formData.append("address", payload.streetAddress || "");
    formData.append("customerId", payload?.userDetails?._id || "");
    formData.append("firstName", payload.firstName || "");
    formData.append("email", payload?.userDetails?.email || "");
    formData.append("deliveryType", "express delivery");
    formData.append("deliveryInstruction", "");
    formData.append("note",  "Order placed from admin panel");
    setIsLoader(true);
    const payStatus = await httpRequest<{
      creditPoints: number;
      aedValue: number;
    }>({
      method: "post",
      path: `/pharma-order/create-order`,
      params: formData,
      header: { "Content-Type": "multipart/form-data" },
    });

    if (payStatus.err) {
      toast(payStatus.err, { type: "error" });
      setIsLoader(false);
      throw new Error(payStatus.err);
    } else {
      toast("Order placed successfully", { type: "success" });
      navigate("/admin/pharmacy/all-orders");
      setIsLoader(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full flex gap-3">
        <div className="w-[60%]">
          <Box
            title="Products"
            icon={<FontAwesomeIcon icon={faBox} size="lg" />}
            className="h-full"
          >
            <div className="flex flex-col gap-3">
              {payload.products.length > 0 ? (
                payload.products.map((product: any, index: number) => (
                  <PrintProduct
                    key={index}
                    product={{
                      ...allPharmaProducts.find(
                        (item: IPharmaInventory) => item._id === product
                      ),
                      quantity: productQuantity[product],
                    }}
                    index={index}
                  />
                ))
              ) : (
                <div className="w-full h-[100px] flex items-center justify-center">
                  <p className="font-Poppins-Regular font-size-16px theme-color-gray">
                    No products added
                  </p>
                </div>
              )}
            </div>
          </Box>
        </div>
        <div className="w-[40%] flex flex-col gap-3">
          <Box
            title="Address Details"
            icon={<FontAwesomeIcon icon={faLocationDot} size="lg" />}
          >
            <div className="flex flex-col gap-2">
              <DetailsLabelValue label="Type" value={payload.address} />
              <DetailsLabelValue label="Emirate" value={payload.emirate} />
              <DetailsLabelValue label="City" value={payload.city} />
              <DetailsLabelValue
                label="Street Address"
                value={payload.streetAddress}
              />
              <DetailsLabelValue
                label="Flat / Villa No"
                value={payload.flatVillaNo}
              />
              <DetailsLabelValue
                label="Building Name"
                value={payload.buildingName}
              />
              <DetailsLabelValue
                label="Additional Information (Area, Landmark)"
                value={payload.additionalInfo}
              />
              <DetailsLabelValue label="Country" value={payload.country} />
            </div>
          </Box>
          <Box
            title="Personal Details"
            icon={<FontAwesomeIcon icon={faLocationDot} size="lg" />}
          >
            <div className="flex flex-col gap-2">
              <DetailsLabelValue
                label="Mobile Number"
                value={payload.mobileNumber}
              />
              <DetailsLabelValue
                label="First & Last Name"
                value={payload.firstName}
              />
            </div>
          </Box>
          <Box
            title="Order Details"
            icon={<FontAwesomeIcon icon={faClipboardList} size="lg" />}
          >
            <div className="flex flex-col gap-2">
              <DetailsLabelValue
                label="Order Total"
                value={`${CURRENCY} ${payload.products
                  .map((product: any) =>
                    allPharmaProducts.find(
                      (item: IPharmaInventory) => item._id === product
                    )
                  )
                  .reduce(
                    (acc: number, curr: any) =>
                      acc +
                      parseFloat(curr?.salePrice || curr?.regularPrice || "0") *
                        (productQuantity[curr?._id] || 1),
                    0
                  )
                  .toFixed(2)}`}
              />
              <DetailsLabelValue label="Shipping" value={`${CURRENCY} 0.00`} />
              <DetailsLabelValue
                label="Total Amount"
                value={`${CURRENCY} ${payload.products
                  .map((product: any) =>
                    allPharmaProducts.find(
                      (item: IPharmaInventory) => item._id === product
                    )
                  )
                  .reduce(
                    (acc: number, curr: any) =>
                      acc +
                      parseFloat(curr?.salePrice || curr?.regularPrice || "0") *
                        (productQuantity[curr?._id] || 1),
                    0
                  )
                  .toFixed(2)}`}
                labelClassName="!text-[18px]"
                className="!text-[18px]"
              />
            </div>
          </Box>
        </div>
      </div>
      <div className="w-full flex justify-end">
        <PrimaryBtn
          ButtonText="Confirm Order"
          onClick={confirmOrder}
          btnExtraStyle={{
            backgroundColor: "#00443F",
            color: "white",
            borderRadius: 50,
          }}
          loader={isLoader}
        />
      </div>
    </div>
  );
};

export default OrderOverview;
